import { ELayoutPosition, EScreenType, ESdChangeReason, SpecialDIIds } from "@kuika/kuika-cl-model";
import { getSdInstance, resolveKuikaIdRef } from "../helpers";
import type { ToolHandlerGroup } from "../types";

/**
 * Collects all Button display items from the screen recursively.
 */
function getAllButtons(sd: any): Array<{ kuikaId: string; friendlyName: string }> {
  const screenDesign = sd.screenProvider.getScreenDesign();
  if (!screenDesign?.displayItems) return [];

  const buttons: Array<{ kuikaId: string; friendlyName: string }> = [];

  function walk(di: any) {
    if (di.item?.name?.toLowerCase() === "button") {
      buttons.push({
        kuikaId: di.id?.toString(),
        friendlyName: di.friendlyName || di.item.name
      });
    }
    if (di.children) {
      for (const child of di.children) {
        walk(child);
      }
    }
  }

  for (const di of screenDesign.displayItems) {
    walk(di);
  }

  buttons.sort((a, b) =>
    (a.friendlyName || "").localeCompare(b.friendlyName || "", undefined, {
      numeric: true,
      sensitivity: "base"
    })
  );

  return buttons;
}

export const screenSettingsTools: ToolHandlerGroup = {
  sync: {
    // ── Press Enter ──────────────────────────────────────────────────

    ui_screen_get_press_enter_button: () => {
      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();
      const buttonId = screenDesign?.onClickActionComponentId;

      if (!buttonId) {
        return { buttonKuikaId: null, message: "No press-enter button assigned" };
      }

      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(buttonId, 10)];

      return {
        buttonKuikaId: buttonId,
        buttonName: di?.friendlyName || di?.item?.name || "Unknown",
        availableButtons: getAllButtons(sd)
      };
    },

    ui_screen_set_press_enter_button: (input) => {
      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();

      const { buttonKuikaId } = input;

      if (buttonKuikaId === null || buttonKuikaId === undefined || buttonKuikaId === "") {
        screenDesign.onClickActionComponentId = undefined;
        sd.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.SetButtonOfRunOnEnter);
        return { success: true, message: "Press-enter button cleared" };
      }

      // Resolve `@alias` / `$N` placeholder refs (e.g. a button emitted by the
      // same ui_render markup that carried `pressEnter`) to the real kuikaId via
      // the cross-call ref map — mirrors ui_style.set / property-tools. A plain
      // numeric kuikaId passes through unchanged.
      const resolvedButtonKuikaId = resolveKuikaIdRef(buttonKuikaId);
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(resolvedButtonKuikaId, 10)];
      if (!di || !di.item) {
        return { error: `Component ${resolvedButtonKuikaId} not found` };
      }

      if (di.item.name?.toLowerCase() !== "button") {
        return { error: `Component ${resolvedButtonKuikaId} is not a Button (found: ${di.item.name})` };
      }

      screenDesign.onClickActionComponentId = resolvedButtonKuikaId.toString();
      sd.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.SetButtonOfRunOnEnter);

      return {
        success: true,
        buttonKuikaId: resolvedButtonKuikaId.toString(),
        buttonName: di.friendlyName || di.item.name,
        message: `Press-enter button set to "${di.friendlyName || di.item.name}" (${resolvedButtonKuikaId})`
      };
    },

    // ── Keyboard Shortcuts ───────────────────────────────────────────

    ui_screen_list_shortcuts: () => {
      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();
      const shortcuts = screenDesign?.shortcuts || {};

      const entries = Object.entries(shortcuts).map(([key, componentId]) => {
        let buttonName: string | undefined;
        if (componentId) {
          const dict = sd.screenProvider.state.screenDesignDictionary;
          const di = dict[parseInt(componentId as string, 10)];
          buttonName = di?.friendlyName || di?.item?.name;
        }
        return {
          keyCombo: key,
          buttonKuikaId: componentId || null,
          buttonName: buttonName || null
        };
      });

      return {
        count: entries.length,
        shortcuts: entries,
        availableButtons: getAllButtons(sd)
      };
    },

    ui_screen_add_shortcut: (input) => {
      const { keyCombo, buttonKuikaId } = input;
      if (!keyCombo) return { error: "keyCombo is required (e.g., 'Ctrl+KeyS', 'Meta+Shift+KeyN')" };

      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();
      if (!screenDesign.shortcuts) {
        screenDesign.shortcuts = {};
      }

      if (screenDesign.shortcuts[keyCombo] !== undefined) {
        return { error: `Shortcut "${keyCombo}" already exists. Use update_shortcut to modify it.` };
      }

      // Validate button exists if provided
      if (buttonKuikaId) {
        const dict = sd.screenProvider.state.screenDesignDictionary;
        const di = dict[parseInt(buttonKuikaId, 10)];
        if (!di || !di.item) return { error: `Component ${buttonKuikaId} not found` };
        if (di.item.name?.toLowerCase() !== "button") {
          return { error: `Component ${buttonKuikaId} is not a Button (found: ${di.item.name})` };
        }
      }

      screenDesign.shortcuts[keyCombo] = buttonKuikaId || "";
      sd.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.UpdateShortcuts);

      return {
        success: true,
        keyCombo,
        buttonKuikaId: buttonKuikaId || null,
        message: `Shortcut "${keyCombo}" added${buttonKuikaId ? ` → button ${buttonKuikaId}` : ""}`
      };
    },

    ui_screen_update_shortcut: (input) => {
      const { keyCombo, newKeyCombo, buttonKuikaId } = input;
      if (!keyCombo) return { error: "keyCombo is required (the existing shortcut key to update)" };

      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();
      if (!screenDesign.shortcuts || screenDesign.shortcuts[keyCombo] === undefined) {
        return { error: `Shortcut "${keyCombo}" not found` };
      }

      // Validate button exists if provided
      if (buttonKuikaId) {
        const dict = sd.screenProvider.state.screenDesignDictionary;
        const di = dict[parseInt(buttonKuikaId, 10)];
        if (!di || !di.item) return { error: `Component ${buttonKuikaId} not found` };
        if (di.item.name?.toLowerCase() !== "button") {
          return { error: `Component ${buttonKuikaId} is not a Button (found: ${di.item.name})` };
        }
      }

      const targetKey = newKeyCombo || keyCombo;

      // If changing the key combo, check for conflicts
      if (newKeyCombo && newKeyCombo !== keyCombo) {
        if (screenDesign.shortcuts[newKeyCombo] !== undefined) {
          return { error: `Shortcut "${newKeyCombo}" already exists` };
        }
        delete screenDesign.shortcuts[keyCombo];
      }

      screenDesign.shortcuts[targetKey] =
        buttonKuikaId !== undefined ? buttonKuikaId || "" : (screenDesign.shortcuts[targetKey] ?? "");
      sd.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.UpdateShortcuts);

      return {
        success: true,
        keyCombo: targetKey,
        buttonKuikaId: screenDesign.shortcuts[targetKey] || null,
        message: `Shortcut updated to "${targetKey}"`
      };
    },

    ui_screen_delete_shortcut: (input) => {
      const { keyCombo } = input;
      if (!keyCombo) return { error: "keyCombo is required" };

      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();
      if (!screenDesign.shortcuts || screenDesign.shortcuts[keyCombo] === undefined) {
        return { error: `Shortcut "${keyCombo}" not found` };
      }

      delete screenDesign.shortcuts[keyCombo];
      sd.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.UpdateShortcuts);

      return {
        success: true,
        keyCombo,
        message: `Shortcut "${keyCombo}" deleted`
      };
    },

    // ── Is Offline ───────────────────────────────────────────────────

    ui_screen_set_offline: (input) => {
      const { enabled } = input;
      if (enabled === undefined || enabled === null) return { error: "enabled (boolean) is required" };

      const sd = getSdInstance();
      const screenId = sd.screenProvider?.state?.sdId;
      if (!screenId) return { error: "No screen loaded" };

      const parsedEnabled = typeof enabled === "string" ? enabled === "true" : !!enabled;

      sd.screenProvider.setScreenAsOfflineScreen(screenId, parsedEnabled);

      return {
        success: true,
        screenId,
        offline: parsedEnabled,
        message: `Offline mode ${parsedEnabled ? "enabled" : "disabled"} for current screen`
      };
    },

    // ── Pull to Refresh ──────────────────────────────────────────────

    ui_screen_set_pull_to_refresh: (input) => {
      const { enabled } = input;
      if (enabled === undefined || enabled === null) return { error: "enabled (boolean) is required" };

      const sd = getSdInstance();
      const screenId = sd.screenProvider?.state?.sdId;
      if (!screenId) return { error: "No screen loaded" };

      const parsedEnabled = typeof enabled === "string" ? enabled === "true" : !!enabled;

      sd.screenProvider.setScreenShouldRefresh(screenId, parsedEnabled);

      return {
        success: true,
        screenId,
        pullToRefresh: parsedEnabled,
        message: `Pull-to-refresh ${parsedEnabled ? "enabled" : "disabled"} for current screen`
      };
    },

    // ── Layout Positions (Master Screens) ────────────────────────────

    ui_screen_get_layout_positions: () => {
      const sd = getSdInstance();
      const screenType = sd.screenProvider.getScreenType();
      if (screenType !== EScreenType.Master) {
        return { error: "Current screen is not a master screen. Layout positions only exist on master screens." };
      }
      return {
        header: sd.screenProvider.getLayoutPosition(SpecialDIIds.layoutHeaderDI),
        footer: sd.screenProvider.getLayoutPosition(SpecialDIIds.layoutFooterDI),
        left: sd.screenProvider.getLayoutPosition(SpecialDIIds.layoutLeftDI),
        right: sd.screenProvider.getLayoutPosition(SpecialDIIds.layoutRightDI),
        legend: "0=invisible, 1=primary (larger), 2=secondary (smaller)"
      };
    },

    ui_screen_set_layout_positions: (input) => {
      const sd = getSdInstance();
      const screenType = sd.screenProvider.getScreenType();
      if (screenType !== EScreenType.Master) {
        return { error: "Current screen is not a master screen. Layout positions can only be set on master screens." };
      }

      const { layout } = input;
      if (!layout) {
        return { error: "layout object is required with header, footer, left, right values (0, 1, or 2)" };
      }

      const header = layout.header ?? ELayoutPosition.invisible;
      const footer = layout.footer ?? ELayoutPosition.invisible;
      const left = layout.left ?? ELayoutPosition.invisible;
      const right = layout.right ?? ELayoutPosition.invisible;

      // Validate values
      const validValues = [ELayoutPosition.invisible, ELayoutPosition.primary, ELayoutPosition.secondary];
      for (const [name, val] of Object.entries({ header, footer, left, right })) {
        if (!validValues.includes(val as ELayoutPosition)) {
          return { error: `Invalid ${name} value: ${val}. Must be 0 (invisible), 1 (primary), or 2 (secondary).` };
        }
      }

      sd.screenProvider.setLayoutPosition(header, footer, left, right);

      return {
        success: true,
        header,
        footer,
        left,
        right,
        message: `Layout positions updated: header=${header}, footer=${footer}, left=${left}, right=${right}`
      };
    }
  }
};
